import { Button } from '@mui/material'
import { GripVertical, X } from "lucide-react"
import React, { useState } from 'react'

// Simple Switch component since UI components were removed
const Switch = ({ checked, onCheckedChange, disabled }: { 
  checked: boolean; 
  onCheckedChange: (checked: boolean) => void; 
  disabled?: boolean; 
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onCheckedChange(!checked)}
    style={{
      position: 'relative',
      width: '44px',
      height: '24px',
      backgroundColor: checked ? '#059669' : '#d1d5db',
      borderRadius: '8px',
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'background-color 0.2s'
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '22px' : '2px',
        width: '20px',
        height: '20px',
        backgroundColor: 'white',
        borderRadius: '50%',
        transition: 'left 0.2s',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
      }}
    />
  </button>
)

interface ViewConfig {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

const defaultViews: ViewConfig[] = [
  {
    id: "overview",
    name: "Overview",
    description: "Key risk metrics, charts, and portfolio summary",
    enabled: true,
  },
  {
    id: "analytics",
    name: "Analytics",
    description: "Detailed analytics and risk decomposition",
    enabled: true,
  },
  {
    id: "factor-lens",
    name: "Factor Lens",
    description: "Factor risk analysis and decomposition",
    enabled: true,
  },
  {
    id: "holdings",
    name: "Holdings",
    description: "Detailed holdings table with sorting and filtering",
    enabled: true,
  },
  {
    id: "what-if",
    name: "What-If",
    description: "Scenario analysis and portfolio simulations",
    enabled: true,
  },
  {
    id: "stress-test",
    name: "Stress Test",
    description: "Stress testing under various market conditions",
    enabled: true,
  },
];

interface ViewSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewSettingsDialog({
  open,
  onOpenChange,
}: ViewSettingsDialogProps) {
  const [views, setViews] = useState<ViewConfig[]>(defaultViews);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const toggleView = (id: string) => {
    const enabledCount = views.filter((v) => v.enabled).length;
    const targetView = views.find((v) => v.id === id);

    if (targetView?.enabled && enabledCount === 1) {
      return;
    }

    setViews(
      views.map((view) =>
        view.id === id
          ? { ...view, enabled: !view.enabled }
          : view,
      ),
    );
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (
    e: React.DragEvent,
    index: number,
  ) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newViews = [...views];
    const draggedItem = newViews[draggedIndex];

    newViews.splice(draggedIndex, 1);
    newViews.splice(index, 0, draggedItem);

    setViews(newViews);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleResetToDefault = () => {
    setViews(defaultViews);
  };

  const handleSaveChanges = () => {
    onOpenChange(false);
  };

  const enabledCount = views.filter((v) => v.enabled).length;

  if (!open) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={() => onOpenChange(false)}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '600px',
          height: '70vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '16px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '24px 24px 32px 24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              margin: 0,
              color: '#111827'
            }}>
              View Settings
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: '4px 0 0 0'
            }}>
              Drag to reorder views and toggle them on/off. At least one view must be enabled.
            </p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              color: '#6b7280'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 24px 16px 24px'
        }}>
          {views.map((view, index) => (
            <div
              key={view.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                backgroundColor: 'white',
                cursor: 'move',
                marginBottom: '12px',
                transition: 'all 0.2s',
                opacity: draggedIndex === index ? 0.5 : 1
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  flex: 1
                }}>
                  <div style={{
                    color: '#9ca3af',
                    marginTop: '2px',
                    flexShrink: 0
                  }}>
                    <GripVertical size={20} />
                  </div>
                  <div style={{
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{
                      fontWeight: '500',
                      color: '#111827',
                      marginBottom: '4px'
                    }}>
                      {view.name}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      color: '#6b7280'
                    }}>
                      {view.description}
                    </div>
                  </div>
                </div>
                <Switch
                  checked={view.enabled}
                  onCheckedChange={() => toggleView(view.id)}
                  disabled={view.enabled && enabledCount === 1}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #e5e7eb',
          padding: '16px 24px'
        }}>
          <div style={{
            fontSize: '16px',
            color: '#6b7280',
            marginBottom: '16px'
          }}>
            {enabledCount} of {views.length} views enabled
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <Button
              variant="outlined"
              onClick={handleResetToDefault}
            >
              Reset to Default
            </Button>
            <div style={{
              display: 'flex',
              gap: '8px'
            }}>
              <Button
                variant="outlined"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                style={{
                  backgroundColor: 'black',
                  color: 'white'
                }}
                onClick={handleSaveChanges}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}